import { useCallback, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { Variable } from '@kentik/ui-shared/boards/boards';

/**
 * URL query parameter prefix used for board variables.
 * e.g. a variable named "Region" becomes the param `var_Region`.
 */
export const URL_VAR_PREFIX = 'var_';

/**
 * Serialises a variable value to a URL-safe string.
 * Multi-value arrays are joined with commas.
 */
function encodeVariableValue(value: unknown): string {
  if (Array.isArray(value)) {
    return (value as unknown[]).map(String).join(',');
  }
  return String(value ?? '');
}

/**
 * Deserialises a raw URL string back into a variable value.
 * When `allowMulti` is true the result is an array; otherwise a plain string.
 */
function decodeVariableValue(encoded: string, allowMulti?: boolean): string | string[] {
  const parts = encoded.split(',').filter(Boolean);
  if (allowMulti) {
    return parts;
  }
  return parts[0] ?? '';
}

/**
 * Returns a new search string that reflects the current variable values while
 * preserving every other query parameter that is already present in the URL.
 * Variables with an empty / null / undefined value have their param removed.
 */
export function buildSearchWithVariables(currentSearch: string, vars: Variable[]): string {
  const params = new URLSearchParams(currentSearch);

  // Remove all existing `var_*` params so stale values don't linger.
  [...params.keys()].filter((k) => k.startsWith(URL_VAR_PREFIX)).forEach((k) => params.delete(k));

  for (const variable of vars) {
    const { name, value } = variable;
    const isEmpty =
      value === undefined ||
      value === null ||
      value === '' ||
      (Array.isArray(value) && (value as unknown[]).length === 0);

    if (!isEmpty) {
      params.set(`${URL_VAR_PREFIX}${name}`, encodeVariableValue(value));
    }
  }

  const str = params.toString();
  return str ? `?${str}` : '';
}

/**
 * Syncs board variable values with URL query parameters (`var_<name>=<value>`).
 *
 * Behaviour:
 * - **On mount** (once variables are available): any `var_*` params present in
 *   the URL are applied to the corresponding variables via `onPresetFiltersChange`.
 * - **On every change**: the wrapped `handlePresetFiltersChange` writes the
 *   updated variable values back to the URL (using `history.replace` so the
 *   current history entry is updated in-place, keeping the back-button usable).
 *
 * Non-variable params (e.g. `q=…`) are preserved untouched.
 */
export function useBoardVariableUrlSync(
  variables: Variable[],
  onPresetFiltersChange: (updatedVariables: Variable[]) => void
): { handlePresetFiltersChange: (updatedVariables: Variable[]) => void } {
  const history = useHistory();

  // Capture the search string at the time the component mounts so we only
  // apply the initial URL state once, even if the search string later changes
  // due to our own writes.
  const initialSearchRef = useRef(history.location.search);
  const appliedUrlParamsRef = useRef(false);

  // Read URL params and seed variable values once variables are available.
  useEffect(() => {
    if (appliedUrlParamsRef.current || variables.length === 0) {
      return;
    }
    appliedUrlParamsRef.current = true;

    const params = new URLSearchParams(initialSearchRef.current);
    const hasVarParams = [...params.keys()].some((k) => k.startsWith(URL_VAR_PREFIX));
    if (!hasVarParams) {
      return;
    }

    const updated = variables.map((v) => {
      const key = `${URL_VAR_PREFIX}${v.name}`;
      const raw = params.get(key);
      return raw !== null ? { ...v, value: decodeVariableValue(raw, v.allowMulti) } : v;
    });

    onPresetFiltersChange(updated);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variables.length]); // Re-runs when variables first become available (0 → N)

  const handlePresetFiltersChange = useCallback(
    (updatedVariables: Variable[]) => {
      onPresetFiltersChange(updatedVariables);

      const newSearch = buildSearchWithVariables(history.location.search, updatedVariables);
      history.replace({ ...history.location, search: newSearch });
    },
    [history, onPresetFiltersChange]
  );

  return { handlePresetFiltersChange };
}
